import { getDisplayOccupancyAmenity } from '../i18n/LocaleHelper';
import { translate } from '../i18n/LocaleProvider';

const MAX_OTHER_AMENITIES = 2;
const MAX_ROOM_AMENITIES = 5;
const MAX_RATE_AMENITIES = 3;
const MAX_AMENITES_WEB = 8;
const MAX_NEW_ROOM_AMENITIES = 20;

const getMaxAmenities = (amenities, num) =>
  amenities
    ? amenities && amenities.length > num
      ? amenities.slice(0, num)
      : amenities
    : [];

const getNewMaxRoomAmenities = amenities =>
  getMaxAmenities(amenities, MAX_NEW_ROOM_AMENITIES);

const getMaxOtherAmenities = amenities =>
  getMaxAmenities(amenities, MAX_OTHER_AMENITIES);

const getMaxRoomAmenities = amenities =>
  getMaxAmenities(amenities, MAX_ROOM_AMENITIES);

export const getAmenityName = ({ amenity, translateFunc }) => {
  const { translationKey, name } = amenity;
  if (translationKey) {
    const fullTranslationKey = `amenityIcon.${translationKey}`;
    const translatedName = translateFunc(fullTranslationKey);
    if (translatedName && translatedName !== fullTranslationKey) {
      return translatedName;
    }
  }
  return name;
};

const mapAmenitiesTranslation = amenities => {
  return amenities.map(amenity => ({
    ...amenity,
    name: getAmenityName({ amenity, translateFunc: translate }),
  }));
};

export const getMaxRateAmenities = amenities =>
  getMaxAmenities(amenities, MAX_RATE_AMENITIES);

export const getMaxNewRoomAmenities = amenities =>
  getNewMaxRoomAmenities(amenities, MAX_NEW_ROOM_AMENITIES);

export const getMaxRateCollapsableAmenities = amenities =>
  getMaxRoomAmenities(amenities, MAX_ROOM_AMENITIES);

const createOccupancyAmenity = occupancy => {
  const occupancyName = occupancy ? getDisplayOccupancyAmenity(occupancy) : '';
  return occupancyName !== ''
    ? { displayId: 'other#people', name: occupancyName }
    : undefined;
};

const createAreaSqmAmenity = areaAmenityName => {
  return areaAmenityName !== ''
    ? { displayId: 'other#zoom-out-map', name: areaAmenityName }
    : undefined;
};

const createRoomOccupancyAmenity = occupancyName => {
  return occupancyName !== ''
    ? { displayId: 'other#people_icon', name: occupancyName }
    : undefined;
};

const wrapOtherAmenities = (occupancy, otherAmenities = []) => {
  const occupancyAmenity = createOccupancyAmenity(occupancy);
  return occupancyAmenity
    ? [occupancyAmenity, ...otherAmenities]
    : otherAmenities;
};

const extraOtherAmenities = (
  areaAmenityName,
  occupancyName,
  otherAmenities = []
) => {
  const areaAmenity = createAreaSqmAmenity(areaAmenityName);
  const occupancyAmenity = createRoomOccupancyAmenity(occupancyName);
  if (otherAmenities.length === 2) {
    const combinedName = `${otherAmenities[0].name.replace(
      ' view',
      ''
    )}/${otherAmenities[1].name.replace(' view', '')} view`;
    otherAmenities[0] = {
      id: otherAmenities[0].id,
      name: combinedName,
      displayId: 'other#city_views',
    };
    otherAmenities.splice(1, 1);
  }
  return areaAmenity || occupancyAmenity
    ? [...otherAmenities, occupancyAmenity, areaAmenity]
    : otherAmenities;
};
export const createRoomLevelAmenitiesMobile = ({
  occupancy,
  otherAmenities,
  roomAmenities,
}) => {
  const finalOtherList = wrapOtherAmenities(occupancy, otherAmenities);
  const displayedRoomAmenities = getMaxRoomAmenities(roomAmenities);

  return {
    displayAmenities: [
      ...getMaxOtherAmenities(finalOtherList),
      ...displayedRoomAmenities,
    ],
    displayedRoomAmenities,
  };
};

export const createExtraRoomLevelAmenitiesMobile = ({
  areaAmenityName,
  occupancyName,
  otherAmenities,
  roomAmenities,
}) => {
  const finalOtherList = extraOtherAmenities(
    areaAmenityName,
    occupancyName,
    otherAmenities
  );

  // Find and extract "Private bathroom" using translationKey
  const privateBathroomAmenity = roomAmenities.find(
    amenity => amenity?.translationKey === 'room_14'
  );

  const formattedPrivateBathroomAmenity = privateBathroomAmenity
    ? {
        id: privateBathroomAmenity.id,
        name: privateBathroomAmenity.name,
        displayId: privateBathroomAmenity.displayId,
      }
    : null;

  // Exclude "Private bathroom" from roomAmenities
  const filteredRoomAmenities = roomAmenities.filter(
    amenity => amenity.translationKey !== 'room_14'
  );

  return {
    extraRoomAmenities: [
      ...finalOtherList,
      formattedPrivateBathroomAmenity,
      ...filteredRoomAmenities,
    ].filter(Boolean),
  };
};

export const createDisplayAmenitiesWeb = ({
  rateAmenities,
  occupancy,
  otherAmenities,
  roomAmenities,
}) => {
  const finalOtherList = wrapOtherAmenities(occupancy, otherAmenities);

  const displayedOtherAmenities = getMaxOtherAmenities(finalOtherList);
  const displayedRateAmenities = mapAmenitiesTranslation(
    getMaxRateAmenities(rateAmenities)
  );

  const excludeRoomAmenities = [
    ...displayedOtherAmenities,
    ...displayedRateAmenities,
  ];

  const numOfRoomAmenities = Math.min(
    MAX_AMENITES_WEB - excludeRoomAmenities.length,
    MAX_ROOM_AMENITIES
  );

  const displayedRoomAmenities = getMaxAmenities(
    roomAmenities,
    numOfRoomAmenities
  );
  return {
    displayAmenities: [
      ...displayedOtherAmenities,
      ...displayedRoomAmenities,
      ...displayedRateAmenities,
    ],
    displayedRoomAmenities,
    displayedRateAmenities,
  };
};

export const AMENITY_ICON_TYPE = {
  HOTEL: 'hotel',
  ROOM: 'room',
  RATE: 'rate',
};

export const getAmenityTranslationKey = (type, id) => `${type}_${id}`;
